import { Box, Button, Card, IconButton, InputAdornment, TextField, Typography } from '@mui/material'
import { IconArrowRight, IconPlus, IconSearch, IconTrash } from '@tabler/icons-react'
import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Column, useFilters, usePagination, useRowSelect, useSortBy, useTable } from 'react-table'
import { FilterButton, Status, TableApiV2, TableHeaderApi } from '~/components'
import { ContentTitle } from '~/components/ContentTitle'
import { useUsers } from '~/contexts'
import { EUserStatus } from '~/graphql/types'
import { SearchQueysData, useDebounce, usePermission, useQueryString, useToggle } from '~/hooks'
import { displayPhone, theme, trimString } from '~/utils'
import { FilterUsersDrawer } from './components/FilterUsersDrawer'

export const UsersList: React.FC = () => {
  const navigate = useNavigate()
  const { users, usersListIsLoading, getUsers } = useUsers()
  const canCreateAndEditUsers = usePermission(['users.updated', 'users.create'])
  const { isTrue: drawerIsOpen, toggle: toggleDrawer, setIsTrue: setDrawerIsOpen } = useToggle(false)

  const queryKeys: SearchQueysData[] = [
    { key: 'status', defaultValue: [], parseValue: true },
    { key: 'accessControlId', defaultValue: [], parseValue: true },
    { key: 'name', defaultValue: '' },
  ]

  const { searchQuerys, paginationQuerySize, searchParams, setSearchParams, page, size } = useQueryString()
  const queryValues = searchQuerys(queryKeys)

  const [filterName, setFilterName] = useState(queryValues.name || '')
  const debouncedFilterName = useDebounce(filterName, 1000)

  useEffect(() => {
    setSearchParams(state => {
      if (debouncedFilterName) {
        state.set('name', debouncedFilterName)
        state.set('page', '0')
      } else {
        state.delete('name')
      }
      return state
    })
  }, [debouncedFilterName])

  useEffect(() => {
    const statusValue = queryValues.status?.[0] as EUserStatus | undefined
    const accessControlIdValue: string[] = queryValues.accessControlId?.length > 0 ? queryValues.accessControlId : []

    const filter = {
      ...queryValues.name?.length > 0 ? { nameOrUsernameOrEmail: queryValues.name.trim() } : {},
      ...statusValue ? { status: statusValue } : {},
      ...accessControlIdValue.length > 0 ? { accessControlId: accessControlIdValue } : {},
    }

    getUsers({
      variables: {
        params: {
          pageSize: Number(size),
          pageNumber: Number(page),
          ...Object.keys(filter).length > 0 ? { filter } : {},
          sort: {
            field: 'createdAt',
            order: -1
          }
        }
      },
      fetchPolicy: 'no-cache',
      onCompleted(result) {
        setSearchParams(state => {
          state.set('items', String(result.userList.meta.documentCounts))
          return state
        })
      },
    })
  }, [searchParams])

  const columns = useMemo((): Column[] => {
    return [
      {
        Header: 'Nome',
        accessor: 'name',
        Cell: ({ value }) => <>{trimString(value, { textCutLength: 40, maxTextLength: 40 })}</>
      },
      {
        Header: 'Status',
        accessor: 'status',
        Cell: ({ value }) => <>{value === 'active' ? <Status color={theme.palette.success.main} text='Ativo' /> : <Status color={theme.palette.error.main} text='Inativo' />}</>
      },
      {
        Header: 'Telefone',
        accessor: 'phone',
        Cell: ({ value }) => <>{displayPhone(value)}</>
      },
      {
        Header: 'Permissão',
        accessor: 'accessControlRef.name',
      },
      {
        Header: 'E-mail',
        accessor: 'email',
      },
      {
        Header: 'Acessar',
        accessor: '_id',
        Cell: ({ value }) => (
          <>
            {canCreateAndEditUsers ? (
              <Link to={`/app/users/update/${value}`}>
                <IconButton>
                  <IconArrowRight />
                </IconButton>
              </Link>
            ) : (
              <IconButton disabled={true}>
                <IconArrowRight />
              </IconButton>
            )}
          </>
        )
      },
    ]
  }, [])

  const dataWithMemo = useMemo(() => users || [], [users])

  const tableMethods = useTable({
    columns,
    data: dataWithMemo,
    manualPagination: true,
    defaultColumn: {
      Filter: <></>
    },
  },
    useFilters,
    useSortBy,
    usePagination,
    useRowSelect,
  )

  return (
    <div>
      <Card sx={{ background: 'white', padding: '0 1rem', borderRadius: '1rem' }}>
        <ContentTitle
          title='Lista de Usuários'
          description='Consulte todos usuários cadastrados'
          breadcrumbLinks={{ currentLink: 'Listar usuários', links: [{ href: '/app/users', label: 'Usuários' }] }}
          rightContent={<Button onClick={() => navigate('/app/users/create')} startIcon={<IconPlus />}>Cadastrar usuário</Button>}
        />
        <TableHeaderApi
          startElement={(
            <>
              <TextField
                fullWidth
                sx={{ maxWidth: '400px' }}
                value={filterName}
                onChange={(e) => setFilterName(e.target.value)}
                placeholder='Procurar um usuário por nome ou E-mail'
                InputProps={{
                  endAdornment: (
                    <>
                      <InputAdornment position='end'>
                        <IconSearch />
                      </InputAdornment>
                    </>
                  )
                }}
              />
            </>
          )}
          endElement={(
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
              {tableMethods.selectedFlatRows.length !== 0 &&
                (
                  <>
                    <Typography color='grey.600'>{tableMethods.selectedFlatRows.length} Selecionado(s)</Typography>
                    <Button sx={{ fontWeight: 600 }} size='small' startIcon={<IconTrash></IconTrash>} color='error'>Excluir</Button>
                  </>
                )
              }
              <FilterButton filtersToClear={queryKeys} toggleDrawer={toggleDrawer} filterCounter={searchParams.size - paginationQuerySize} />
            </Box>
          )}
        />
        <TableApiV2
          isLoading={usersListIsLoading}
          tableMethods={tableMethods}
          columns={columns}
        />
      </Card>
      <FilterUsersDrawer queryKeys={queryKeys} drawerIsOpen={drawerIsOpen} setDrawerIsOpen={setDrawerIsOpen} toggleDrawer={toggleDrawer} />
    </div>
  )
}
